import { Check, X } from 'lucide-react'
import { EmptyValue } from '@/components/custom/empty-value'
import { createMemoCell } from '@/lib/create-memo-cell'
import { cn } from '@/lib/utils'
import type { CellProps } from '../types'

interface CellBooleanProps extends CellProps<
  boolean | string | null | undefined
> {
  /** Display mode */
  mode?: 'icon' | 'text' | 'badge'
  /** Text for true value */
  trueText?: string
  /** Text for false value */
  falseText?: string
}

function CellBooleanBase({
  value,
  mode = 'icon',
  trueText = 'Yes',
  falseText = 'No',
  className,
}: CellBooleanProps) {
  // Normalize value to boolean
  const boolValue = normalizeBooleanValue(value)

  if (boolValue === null) {
    return <EmptyValue />
  }

  switch (mode) {
    case 'text':
      return (
        <span
          className={cn(
            boolValue ? 'text-green-600' : 'text-gray-500',
            className
          )}
        >
          {boolValue ? trueText : falseText}
        </span>
      )

    case 'badge':
      return (
        <span
          className={cn(
            'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium',
            boolValue
              ? 'bg-green-100 text-green-800'
              : 'bg-gray-100 text-gray-800',
            className
          )}
        >
          {boolValue ? trueText : falseText}
        </span>
      )

    default:
      return (
        <div className={cn('flex items-center justify-start', className)}>
          {boolValue ? (
            <Check className="h-4 w-4 text-green-600" />
          ) : (
            <X className="h-4 w-4 text-gray-400" />
          )}
        </div>
      )
  }
}

function normalizeBooleanValue(
  value: boolean | string | null | undefined
): boolean | null {
  if (value === null || value === undefined) {
    return null
  }

  if (typeof value === 'boolean') {
    return value
  }

  const strValue = String(value).toLowerCase().trim()

  if (['true', 'yes', '1', 'on'].includes(strValue)) {
    return true
  }

  if (['false', 'no', '0', 'off'].includes(strValue)) {
    return false
  }

  return null
}

export const CellBoolean = createMemoCell(CellBooleanBase, 'CellBoolean')
